/**
 * Agent Pool Selection Layer
 * 
 * Selects a fair subset of agents (5-10) per lead before routing.
 * This layer runs BEFORE the auction/routing engine.
 * 
 * Key concepts:
 * - Monthly delivery targets for pacing
 * - Goal-aware pacing to prioritize agents behind schedule
 * - Pace deficit scoring for fair opportunity distribution
 * - Rotating buckets for additional fairness
 */

import type { MockAgent } from '@/contexts/MockAgentsContext';
import type { AgentContractPressure } from '@/contexts/ContractsContext';
import type { GuardrailInputs } from './executionGuardrails';
import { applyPoolBoost } from './executionGuardrails';
import type { PricingRuleForRouting } from './leadRouter';
import { normalizeLeadTypeId } from './leads/leadTypeIds';

// --- Type Definitions ---

export type PoolStrategy = 'ROTATING_BUCKETS_WITH_PACING' | 'GOAL_AWARE_PACING';

export interface DeliveryTarget {
  monthlyTarget: number;
  deliveredThisMonth: number;
  cycleStartDate: number; // timestamp
  cycleLengthDays: number;
  // Guaranteed minimum enforcement
  guaranteedMinimum?: number; // minimum leads guaranteed per cycle
}

export interface AgentWithPacing extends MockAgent {
  deliveryTarget?: DeliveryTarget;
}

export interface PoolSelectionInput {
  lead: {
    id: string;
    leadTypeId: string;
    state: string;
    vertical: 'life_insurance' | 'mortgage';
  };
  allAgents: AgentWithPacing[];
  targetPoolSize?: number; // Default: 8
  strategy?: PoolStrategy;
  // Execution guardrails from Delivery Control
  agentGuardrails?: Map<string, GuardrailInputs>;
  // Optional pricing rules (used to infer delivery tier multipliers)
  getPricingRule?: (leadTypeId: string, state: string) => PricingRuleForRouting | null;
  // Optional contract pressures (used to prioritize expiring contracts)
  contractPressures?: Map<string, AgentContractPressure>;
}

export interface AgentPacingScore {
  agentId: string;
  agentName: string;
  pacingScore: number;
  remainingLeads: number;
  daysRemaining: number;
  deliveryProgress: number; // percentage
  isBehindTarget: boolean;
  // Goal-aware pacing fields
  expectedDeliveredByNow?: number;
  paceDeficit?: number;
  paceDeficitScore?: number;
  // Delivery pressure fields (guaranteed minimum enforcement)
  guaranteedMinimum?: number;
  remainingGuaranteedLeads?: number;
  deliveryPressureScore?: number;
  // Contract pressure fields (contract fulfillment urgency)
  contractPressureScore?: number;
}

export interface PoolMeta {
  strategy: PoolStrategy;
  poolSize: number;
  bucketId: string;
  bucketIndex: number;
  totalBuckets: number;
  pacingBoostApplied: boolean;
  pacingScores: AgentPacingScore[];
  preFilteredCount: number;
  postFilteredCount: number;
  selectionTimestamp: number;
  poolAgentIds?: string[]; // Added after selection for logging
  // Goal-aware pacing fields
  goalAwarePacing?: {
    agentsBehindPace: number;
    agentsAheadOfPace: number;
    averagePaceDeficit: number;
    dynamicPoolExpansion: boolean;
    poolSelectionReason: string;
  };
  // Delivery pressure enforcement fields
  deliveryPressure?: {
    agentsWithGuarantee: number;
    agentsUnderPressure: number;
    averagePressureScore: number;
    pressureBoostApplied: boolean;
    totalGuaranteedDemand: number;
    demandExceedsSupplyWarning: boolean;
  };
  // Contract pressure enforcement fields
  contractPressure?: {
    agentsWithContracts: number;
    agentsUnderPressure: number;
    totalCommitted: number;
    totalDelivered: number;
    averageUrgencyScore: number;
    atRiskContracts: number;
    contractPressureApplied: boolean;
  };
  // Execution guardrails applied
  executionGuardrails?: {
    guardrailsApplied: boolean;
    agentsWithPoolBoost: number;
    totalBoostApplied: number;
  };
}

export interface PoolSelectionResult {
  poolAgents: AgentWithPacing[];
  poolMeta: PoolMeta;
}

// --- Constants ---

const DEFAULT_POOL_SIZE = 8;
const MIN_POOL_SIZE = 5;
const MAX_POOL_SIZE = 10;
const BUCKET_SIZE = 10;

// Goal-aware pacing weights
const PACING_WEIGHT = 0.6;
const ROTATION_WEIGHT = 0.2;

// Delivery pressure weights (guaranteed minimum enforcement)
const PRESSURE_BOOST_WEIGHT = 1.0; // Stronger delivery pressure boost for MVP
const PRESSURE_THRESHOLD = 0.3; // Pressure score threshold for "under pressure"
const CONTRACT_PRESSURE_WEIGHT = 1.2; // Stronger contract urgency influence for MVP

// Dynamic pool expansion threshold
const BEHIND_PACE_THRESHOLD = 0.3;
const AGENTS_BEHIND_THRESHOLD = 3;
const POOL_EXPANSION_SIZE = 2;

// --- Helper Functions ---

/**
 * Clamp a value between min and max.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Calculate pacing score for an agent with goal-aware metrics and delivery pressure.
 * Higher score = more behind target = higher priority for pool selection.
 */
function calculatePacingScore(agent: AgentWithPacing, useGoalAware: boolean = false): AgentPacingScore {
  const target = agent.deliveryTarget;
  
  if (!target || target.monthlyTarget <= 0) {
    // No target configured - neutral pacing score
    return {
      agentId: agent.id,
      agentName: agent.name,
      pacingScore: 1,
      remainingLeads: 0,
      daysRemaining: 0,
      deliveryProgress: 100,
      isBehindTarget: false,
      expectedDeliveredByNow: 0,
      paceDeficit: 0,
      paceDeficitScore: 0,
      guaranteedMinimum: undefined,
      remainingGuaranteedLeads: undefined,
      deliveryPressureScore: 0,
    };
  }
  
  const now = Date.now();
  const cycleEndDate = target.cycleStartDate + (target.cycleLengthDays * 24 * 60 * 60 * 1000);
  const daysRemaining = Math.max(1, Math.ceil((cycleEndDate - now) / (24 * 60 * 60 * 1000)));
  const remainingLeads = Math.max(0, target.monthlyTarget - target.deliveredThisMonth);
  
  // Basic pacing score: leads needed per day remaining
  const basicPacingScore = remainingLeads / daysRemaining;
  
  // Calculate progress percentage
  const deliveryProgress = (target.deliveredThisMonth / target.monthlyTarget) * 100;
  
  // Calculate elapsed days
  const elapsedMs = now - target.cycleStartDate;
  const elapsedDays = Math.max(0, elapsedMs / (24 * 60 * 60 * 1000));
  
  // Goal-aware pacing: expected delivery by now
  const expectedDeliveredByNow = target.monthlyTarget * (elapsedDays / target.cycleLengthDays);
  
  // Pace deficit: positive = behind schedule, negative = ahead
  const paceDeficit = expectedDeliveredByNow - target.deliveredThisMonth;
  
  // Normalize to -1 to 1 range
  const paceDeficitScore = clamp(
    paceDeficit / Math.max(target.monthlyTarget, 1),
    -1,
    1
  );
  
  // Expected progress percentage
  const expectedProgress = (elapsedDays / target.cycleLengthDays) * 100;
  const isBehindTarget = deliveryProgress < expectedProgress;
  
  // Delivery Pressure Score (guaranteed minimum enforcement)
  let deliveryPressureScore = 0;
  let remainingGuaranteedLeads: number | undefined = undefined;
  
  if (target.guaranteedMinimum && target.guaranteedMinimum > 0) {
    remainingGuaranteedLeads = Math.max(0, target.guaranteedMinimum - target.deliveredThisMonth);
    // deliveryPressureScore = remainingGuaranteedLeads / daysRemaining, clamped to [0, 1]
    deliveryPressureScore = clamp(
      remainingGuaranteedLeads / Math.max(daysRemaining, 1),
      0,
      1
    );
  }
  
  // For goal-aware strategy, use pace deficit score as main metric
  const pacingScore = useGoalAware 
    ? Math.round((paceDeficitScore + 1) * 50) / 100 // Normalize to 0-1 range for compatibility
    : Math.round(basicPacingScore * 100) / 100;
  
  return {
    agentId: agent.id,
    agentName: agent.name,
    pacingScore,
    remainingLeads,
    daysRemaining,
    deliveryProgress: Math.round(deliveryProgress * 10) / 10,
    isBehindTarget,
    expectedDeliveredByNow: Math.round(expectedDeliveredByNow * 10) / 10,
    paceDeficit: Math.round(paceDeficit * 10) / 10,
    paceDeficitScore: Math.round(paceDeficitScore * 1000) / 1000,
    guaranteedMinimum: target.guaranteedMinimum,
    remainingGuaranteedLeads,
    deliveryPressureScore: Math.round(deliveryPressureScore * 1000) / 1000,
  };
}

/**
 * Pre-filter agents based on basic eligibility (before detailed routing checks).
 * This is a lightweight filter - full eligibility is checked in the routing engine.
 */
function preFilterAgents(
  agents: AgentWithPacing[],
  leadTypeId: string,
  leadState: string,
  leadVertical: 'life_insurance' | 'mortgage'
): AgentWithPacing[] {
  const canonicalLeadTypeId = normalizeLeadTypeId(leadTypeId);
  return agents.filter(agent => {
    if (agent.isActive === false) return false;
    
    // Check vertical compatibility
    if (agent.vertical !== leadVertical) return false;
    
    // Check lead type is active
    const pref = agent.leadPreferences.find(
      p => normalizeLeadTypeId(p.leadTypeId) === canonicalLeadTypeId
    );
    if (!pref?.isActive) return false;
    
    // Check licensed in state
    if (!agent.licensedStates.includes(leadState)) return false;
    
    // Check has capacity (not over caps)
    if (agent.leadsReceivedToday >= pref.dailyCap) return false;
    if (agent.leadsReceivedThisMonth >= pref.monthlyCap) return false;
    
    return true;
  });
}

/**
 * Generate a deterministic bucket ID based on lead properties.
 * Used for consistent bucket rotation.
 */
function generateBucketId(leadId: string, leadTypeId: string): string {
  // Simple hash using lead properties
  const combined = `${leadId}-${leadTypeId}`;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `bucket-${Math.abs(hash)}`;
}

/**
 * Divide agents into buckets and select the appropriate bucket.
 */
function selectBucket(
  agents: AgentWithPacing[],
  bucketId: string,
  bucketSize: number = BUCKET_SIZE
): { bucket: AgentWithPacing[]; bucketIndex: number; totalBuckets: number } {
  if (agents.length <= bucketSize) {
    return { bucket: agents, bucketIndex: 0, totalBuckets: 1 };
  }
  
  // Sort agents by ID for consistent ordering
  const sortedAgents = [...agents].sort((a, b) => a.id.localeCompare(b.id));
  
  // Calculate total buckets
  const totalBuckets = Math.ceil(sortedAgents.length / bucketSize);
  
  // Determine bucket index from bucket ID
  let bucketIndex = 0;
  for (let i = 0; i < bucketId.length; i++) {
    bucketIndex += bucketId.charCodeAt(i);
  }
  bucketIndex = bucketIndex % totalBuckets;
  
  // Extract bucket
  const start = bucketIndex * bucketSize;
  const end = Math.min(start + bucketSize, sortedAgents.length);
  const bucket = sortedAgents.slice(start, end);
  
  return { bucket, bucketIndex, totalBuckets };
}

/**
 * Select agents from bucket based on pacing scores (legacy strategy).
 * Agents behind target have higher probability of selection.
 */
function selectWithPacing(
  bucket: AgentWithPacing[],
  targetSize: number,
  pacingScores: Map<string, AgentPacingScore>
): { selected: AgentWithPacing[]; pacingBoostApplied: boolean } {
  if (bucket.length <= targetSize) {
    return { selected: bucket, pacingBoostApplied: false };
  }
  
  // Sort by pacing score (descending - higher score = more behind)
  const sortedByPacing = [...bucket].sort((a, b) => {
    const scoreA = pacingScores.get(a.id)?.pacingScore ?? 0;
    const scoreB = pacingScores.get(b.id)?.pacingScore ?? 0;
    return scoreB - scoreA;
  });
  
  // Check if any agent is behind target
  const anyBehindTarget = sortedByPacing.some(agent => 
    pacingScores.get(agent.id)?.isBehindTarget
  );
  
  if (!anyBehindTarget) {
    // No pacing boost needed - use first N agents (already sorted by ID consistency)
    return { selected: sortedByPacing.slice(0, targetSize), pacingBoostApplied: false };
  }
  
  // Apply pacing boost: prioritize agents behind target
  const behindTarget = sortedByPacing.filter(a => pacingScores.get(a.id)?.isBehindTarget);
  const onTrack = sortedByPacing.filter(a => !pacingScores.get(a.id)?.isBehindTarget);
  
  // Take as many behind-target agents as possible, fill rest with on-track
  const selected: AgentWithPacing[] = [];
  
  // Add behind-target agents first (up to target size)
  for (const agent of behindTarget) {
    if (selected.length >= targetSize) break;
    selected.push(agent);
  }
  
  // Fill remaining slots with on-track agents
  for (const agent of onTrack) {
    if (selected.length >= targetSize) break;
    selected.push(agent);
  }
  
  return { selected, pacingBoostApplied: true };
}

/**
 * Goal-aware pool selection with pace deficit scoring and delivery pressure enforcement.
 * Strongly favors agents behind schedule and those with guaranteed minimums.
 * Now supports execution guardrails for pool inclusion boost.
 */
function selectWithGoalAwarePacing(
  agents: AgentWithPacing[],
  targetSize: number,
  pacingScores: Map<string, AgentPacingScore>,
  bucketIndex: number,
  agentGuardrails?: Map<string, GuardrailInputs>,
  getPricingRule?: (leadTypeId: string, state: string) => PricingRuleForRouting | null,
  leadTypeId?: string,
  leadState?: string,
  contractPressures?: Map<string, AgentContractPressure>
): { 
  selected: AgentWithPacing[]; 
  pacingBoostApplied: boolean;
  dynamicPoolExpansion: boolean;
  poolSelectionReason: string;
  agentsBehindPace: number;
  agentsAheadOfPace: number;
  averagePaceDeficit: number;
  // Delivery pressure stats
  deliveryPressure: {
    agentsWithGuarantee: number;
    agentsUnderPressure: number;
    averagePressureScore: number;
    pressureBoostApplied: boolean;
    totalGuaranteedDemand: number;
    demandExceedsSupplyWarning: boolean;
  };
  // Execution guardrails stats
  executionGuardrails: {
    guardrailsApplied: boolean;
    agentsWithPoolBoost: number;
    totalBoostApplied: number;
  };
} {
  // Calculate goal-aware stats
  let totalPaceDeficit = 0;
  let agentsBehindPace = 0;
  let agentsAheadOfPace = 0;
  
  // Delivery pressure stats
  let agentsWithGuarantee = 0;
  let agentsUnderPressure = 0;
  let totalPressureScore = 0;
  let totalGuaranteedDemand = 0;
  
  for (const agent of agents) {
    const score = pacingScores.get(agent.id);
    if (score) {
      totalPaceDeficit += score.paceDeficit ?? 0;
      if ((score.paceDeficitScore ?? 0) > 0) {
        agentsBehindPace++;
      } else {
        agentsAheadOfPace++;
      }
      
      // Track delivery pressure
      if (score.guaranteedMinimum && score.guaranteedMinimum > 0) {
        agentsWithGuarantee++;
        totalGuaranteedDemand += score.remainingGuaranteedLeads ?? 0;
        if ((score.deliveryPressureScore ?? 0) > 0) {
          agentsUnderPressure++;
          totalPressureScore += score.deliveryPressureScore ?? 0;
        }
      }
    }
  }
  
  const averagePaceDeficit = agents.length > 0 
    ? Math.round((totalPaceDeficit / agents.length) * 10) / 10 
    : 0;
  
  const averagePressureScore = agentsWithGuarantee > 0
    ? Math.round((totalPressureScore / agentsWithGuarantee) * 1000) / 1000
    : 0;
  
  // Check if guaranteed demand might exceed supply (flag but don't break)
  const estimatedDailySupply = 10; // Rough estimate for warning purposes
  const demandExceedsSupplyWarning = totalGuaranteedDemand > estimatedDailySupply * 5;
  
  // Check for dynamic pool expansion (triggered by behind-pace OR high pressure agents)
  const highDeficitAgents = agents.filter(a => {
    const score = pacingScores.get(a.id);
    return (score?.paceDeficitScore ?? 0) > BEHIND_PACE_THRESHOLD;
  });
  
  const highPressureAgents = agents.filter(a => {
    const score = pacingScores.get(a.id);
    return (score?.deliveryPressureScore ?? 0) > PRESSURE_THRESHOLD;
  });
  
  const dynamicPoolExpansion = highDeficitAgents.length >= AGENTS_BEHIND_THRESHOLD ||
    highPressureAgents.length >= AGENTS_BEHIND_THRESHOLD;
  
  const effectivePoolSize = dynamicPoolExpansion 
    ? Math.min(targetSize + POOL_EXPANSION_SIZE, MAX_POOL_SIZE)
    : targetSize;
  
  const pressureBoostApplied = agentsUnderPressure > 0;
  
  // If pool can fit all agents, return all
  if (agents.length <= effectivePoolSize) {
    return {
      selected: agents,
      pacingBoostApplied: agentsBehindPace > 0,
      dynamicPoolExpansion,
      poolSelectionReason: 'all_agents_fit',
      agentsBehindPace,
      agentsAheadOfPace,
      averagePaceDeficit,
      deliveryPressure: {
        agentsWithGuarantee,
        agentsUnderPressure,
        averagePressureScore,
        pressureBoostApplied,
        totalGuaranteedDemand,
        demandExceedsSupplyWarning,
      },
      executionGuardrails: {
        guardrailsApplied: false,
        agentsWithPoolBoost: 0,
        totalBoostApplied: 0,
      },
    };
  }
  
  // Track guardrails applied
  let agentsWithPoolBoost = 0;
  let totalBoostApplied = 0;
  
  // Compute final pool score for each agent
  // Now includes delivery pressure and execution guardrails as factors
  const agentsWithScores = agents.map((agent, index) => {
    const pacingScore = pacingScores.get(agent.id);
    const paceDeficitScore = pacingScore?.paceDeficitScore ?? 0;
    const deliveryPressureScore = pacingScore?.deliveryPressureScore ?? 0;
    const contractPressureScore = contractPressures?.get(agent.id)?.contractPressureScore ?? 0;
    
    // Rotation score based on agent position relative to bucket
    const rotationScore = ((index + bucketIndex) % agents.length) / Math.max(agents.length - 1, 1);
    
    // Final score: weighted combination with delivery pressure boost
    // paceDeficitScore is -1 to 1, we shift to 0-2 for positive weighting
    const normalizedPaceScore = (paceDeficitScore + 1); // 0 to 2
    
    // Delivery pressure adds additional priority (0 to PRESSURE_BOOST_WEIGHT)
    const pressureBoost = deliveryPressureScore * PRESSURE_BOOST_WEIGHT;
    
    let finalPoolScore = (normalizedPaceScore * PACING_WEIGHT) + 
      (rotationScore * ROTATION_WEIGHT) + 
      pressureBoost + 
      (contractPressureScore * CONTRACT_PRESSURE_WEIGHT);

    // Velocity/bid multipliers removed for MVP (contract pressure + pacing only)
    
    // Apply execution guardrail pool boost if available
    const guardrailInputs = agentGuardrails?.get(agent.id);
    if (guardrailInputs && guardrailInputs.poolInclusionBoost > 0) {
      const boostResult = applyPoolBoost(finalPoolScore, guardrailInputs.poolInclusionBoost);
      if (boostResult.applied) {
        finalPoolScore = boostResult.adjustedScore;
        agentsWithPoolBoost++;
        totalBoostApplied += boostResult.appliedBoost;
        
        if (import.meta.env.MODE !== 'production') {
          console.log(`[Guardrails] Agent ${agent.name}: pool score boosted by ${(boostResult.appliedBoost * 100).toFixed(0)}%`);
        }
      }
    }
    
    return {
      agent,
      paceDeficitScore,
      deliveryPressureScore,
      rotationScore,
      finalPoolScore,
    };
  });
  
  // Sort by final pool score (descending - higher = more priority)
  agentsWithScores.sort((a, b) => b.finalPoolScore - a.finalPoolScore);
  
  // Select top N agents
  const selected = agentsWithScores.slice(0, effectivePoolSize).map(a => a.agent);
  
  // Determine selection reason
  let poolSelectionReason = 'balanced_selection';
  if (agentsWithPoolBoost > 0) {
    poolSelectionReason = 'execution_guardrails_applied';
  } else if (agentsUnderPressure > 0) {
    poolSelectionReason = 'delivery_pressure_applied';
  } else if (agentsBehindPace > agentsAheadOfPace * 2) {
    poolSelectionReason = 'majority_behind_target';
  } else if (dynamicPoolExpansion) {
    poolSelectionReason = 'dynamic_expansion_triggered';
  } else if (agentsBehindPace === 0) {
    poolSelectionReason = 'all_agents_on_track';
  }
  
  return {
    selected,
    pacingBoostApplied: agentsBehindPace > 0,
    dynamicPoolExpansion,
    poolSelectionReason,
    agentsBehindPace,
    agentsAheadOfPace,
    averagePaceDeficit,
    deliveryPressure: {
      agentsWithGuarantee,
      agentsUnderPressure,
      averagePressureScore,
      pressureBoostApplied,
      totalGuaranteedDemand,
      demandExceedsSupplyWarning,
    },
    executionGuardrails: {
      guardrailsApplied: agentsWithPoolBoost > 0,
      agentsWithPoolBoost,
      totalBoostApplied: Math.round(totalBoostApplied * 100) / 100,
    },
  };
}

// --- Main Selection Function ---

/**
 * Select a fair pool of agents for a lead.
 * 
 * Strategies:
 * - ROTATING_BUCKETS_WITH_PACING: Legacy bucket rotation with pacing boost
 * - GOAL_AWARE_PACING: Goal-aware selection with pace deficit scoring (recommended)
 */
export function selectAgentPool(input: PoolSelectionInput): PoolSelectionResult {
  const {
    lead,
    allAgents,
    targetPoolSize = DEFAULT_POOL_SIZE,
    strategy = 'GOAL_AWARE_PACING', // Default to new strategy
    agentGuardrails,
    getPricingRule,
    contractPressures,
  } = input;
  
  const effectivePoolSize = Math.min(MAX_POOL_SIZE, Math.max(MIN_POOL_SIZE, targetPoolSize));
  const selectionTimestamp = Date.now();
  const useGoalAware = strategy === 'GOAL_AWARE_PACING';
  
  // Step 1: Pre-filter agents
  const preFiltered = preFilterAgents(allAgents, lead.leadTypeId, lead.state, lead.vertical);
  
  // If no eligible agents, return empty pool
  if (preFiltered.length === 0) {
    return {
      poolAgents: [],
      poolMeta: {
        strategy,
        poolSize: 0,
        bucketId: 'none',
        bucketIndex: 0,
        totalBuckets: 0,
        pacingBoostApplied: false,
        pacingScores: [],
        preFilteredCount: allAgents.length,
        postFilteredCount: 0,
        selectionTimestamp,
      },
    };
  }
  
  // Step 2: Calculate pacing scores for all pre-filtered agents
  const pacingScoresMap = new Map<string, AgentPacingScore>();
  const pacingScoresList: AgentPacingScore[] = [];
  
  for (const agent of preFiltered) {
    const score = calculatePacingScore(agent, useGoalAware);
    const contractPressureScore = contractPressures?.get(agent.id)?.contractPressureScore;
    if (contractPressureScore !== undefined) {
      score.contractPressureScore = contractPressureScore;
    }
    pacingScoresMap.set(agent.id, score);
    pacingScoresList.push(score);
  }
  
  // Step 3: Generate bucket ID for rotation component
  const bucketId = generateBucketId(lead.id, lead.leadTypeId);
  const { bucketIndex, totalBuckets } = selectBucket(preFiltered, bucketId);
  
  // Step 4: Select agents based on strategy
  if (useGoalAware) {
    // Goal-aware pacing: use pace deficit scoring with guardrails
    const goalAwareResult = selectWithGoalAwarePacing(
      preFiltered,
      effectivePoolSize,
      pacingScoresMap,
      bucketIndex,
      agentGuardrails,
      getPricingRule,
      lead.leadTypeId,
      lead.state,
      contractPressures
    );
    
    return {
      poolAgents: goalAwareResult.selected,
      poolMeta: {
        strategy,
        poolSize: goalAwareResult.selected.length,
        bucketId,
        bucketIndex,
        totalBuckets,
        pacingBoostApplied: goalAwareResult.pacingBoostApplied,
        pacingScores: goalAwareResult.selected.map(a => pacingScoresMap.get(a.id)!),
        preFilteredCount: allAgents.length,
        postFilteredCount: preFiltered.length,
        selectionTimestamp,
        goalAwarePacing: {
          agentsBehindPace: goalAwareResult.agentsBehindPace,
          agentsAheadOfPace: goalAwareResult.agentsAheadOfPace,
          averagePaceDeficit: goalAwareResult.averagePaceDeficit,
          dynamicPoolExpansion: goalAwareResult.dynamicPoolExpansion,
          poolSelectionReason: goalAwareResult.poolSelectionReason,
        },
        deliveryPressure: goalAwareResult.deliveryPressure,
        executionGuardrails: goalAwareResult.executionGuardrails.guardrailsApplied 
          ? goalAwareResult.executionGuardrails 
          : undefined,
      },
    };
  }
  
  // Legacy strategy: rotating buckets with pacing
  const { bucket } = selectBucket(preFiltered, bucketId);
  const { selected, pacingBoostApplied } = selectWithPacing(
    bucket,
    effectivePoolSize,
    pacingScoresMap
  );
  
  return {
    poolAgents: selected,
    poolMeta: {
      strategy,
      poolSize: selected.length,
      bucketId,
      bucketIndex,
      totalBuckets,
      pacingBoostApplied,
      pacingScores: selected.map(a => pacingScoresMap.get(a.id)!),
      preFilteredCount: allAgents.length,
      postFilteredCount: preFiltered.length,
      selectionTimestamp,
    },
  };
}

// --- Utility Functions ---

/**
 * Get a summary of pool selection for logging/debugging.
 */
export function explainPoolSelection(result: PoolSelectionResult): string {
  const { poolAgents, poolMeta } = result;
  
  if (poolAgents.length === 0) {
    return `No eligible agents found for pool selection. ${poolMeta.preFilteredCount} agents evaluated, 0 passed pre-filter.`;
  }
  
  const behindCount = poolMeta.pacingScores.filter(s => s.isBehindTarget).length;
  
  if (poolMeta.strategy === 'GOAL_AWARE_PACING' && poolMeta.goalAwarePacing) {
    const { agentsBehindPace, averagePaceDeficit, dynamicPoolExpansion, poolSelectionReason } = poolMeta.goalAwarePacing;
    return `Goal-aware pool: ${poolAgents.length} agents. ` +
      `${agentsBehindPace} behind pace (avg deficit: ${averagePaceDeficit}). ` +
      `Reason: ${poolSelectionReason}. ` +
      `Dynamic expansion: ${dynamicPoolExpansion ? 'Yes' : 'No'}.`;
  }
  
  return `Pool selected: ${poolAgents.length} agents from bucket ${poolMeta.bucketIndex + 1}/${poolMeta.totalBuckets}. ` +
    `Strategy: ${poolMeta.strategy}. ` +
    `${poolMeta.postFilteredCount}/${poolMeta.preFilteredCount} pre-filtered. ` +
    `Pacing boost: ${poolMeta.pacingBoostApplied ? 'Yes' : 'No'} (${behindCount} behind target).`;
}

/**
 * Create default delivery target for a new agent.
 */
export function createDefaultDeliveryTarget(monthlyTarget: number = 100): DeliveryTarget {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  
  return {
    monthlyTarget,
    deliveredThisMonth: 0,
    cycleStartDate: firstOfMonth.getTime(),
    cycleLengthDays: daysInMonth,
  };
}

/**
 * Get a detailed breakdown of goal-aware pacing for an agent.
 */
export function getAgentPacingDetails(agent: AgentWithPacing): {
  hasTarget: boolean;
  expectedDeliveredByNow: number;
  paceDeficit: number;
  paceDeficitScore: number;
  paceStatus: 'behind' | 'on_track' | 'ahead';
  daysRemaining: number;
  leadsNeededPerDay: number;
} {
  const score = calculatePacingScore(agent, true);
  
  const paceStatus: 'behind' | 'on_track' | 'ahead' = 
    (score.paceDeficitScore ?? 0) > 0.1 ? 'behind' :
    (score.paceDeficitScore ?? 0) < -0.1 ? 'ahead' : 'on_track';
  
  const leadsNeededPerDay = score.daysRemaining > 0 
    ? Math.round((score.remainingLeads / score.daysRemaining) * 10) / 10
    : 0;
  
  return {
    hasTarget: !!agent.deliveryTarget && agent.deliveryTarget.monthlyTarget > 0,
    expectedDeliveredByNow: score.expectedDeliveredByNow ?? 0,
    paceDeficit: score.paceDeficit ?? 0,
    paceDeficitScore: score.paceDeficitScore ?? 0,
    paceStatus,
    daysRemaining: score.daysRemaining,
    leadsNeededPerDay,
  };
}
